import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Entrada, Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post, getTodos } from '../../nucleo/api';
import { dinheiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarEstoque } from './comum';
import type { Deposito } from './tipos';
import { anunciarOcultos } from '../../componentes/sensivel';

/** M9.2: depósitos e os locais (endereços) de cada um. */
export function Depositos() {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState<Deposito | 'novo' | null>(null);
  const [apagar, setApagar] = useState<Deposito | null>(null);
  const [apagarLocal, setApagarLocal] = useState<{ id: string; codigo: string } | null>(null);
  const [novoLocal, setNovoLocal] = useState<Record<string, string>>({});
  const q = useQuery({ queryKey: ['/estoque/depositos', 'tela'], queryFn: () => getTodos<Deposito>('/estoque/depositos') });
  const erro = (e: unknown) => toast.erro(mensagemDeErro(e));
  const recarregar = () => invalidarEstoque(qc);

  const padrao = useMutation({ mutationFn: (id: string) => patch(`/estoque/depositos/${id}`, { padrao: true }), onSuccess: recarregar, onError: erro });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/estoque/depositos/${id}`),
    onSuccess: () => {
      setApagar(null);
      return recarregar();
    },
    onError: (e) => {
      setApagar(null);
      erro(e);
    },
  });
  const addLocal = useMutation({
    mutationFn: (v: { deposito: string; codigo: string }) => post('/estoque/locais', { deposito_id: v.deposito, codigo: v.codigo }),
    onSuccess: (_r, v) => {
      setNovoLocal((x) => ({ ...x, [v.deposito]: '' }));
      return recarregar();
    },
    onError: erro,
  });
  const rmLocal = useMutation({
    mutationFn: (id: string) => del(`/estoque/locais/${id}`),
    onSuccess: () => {
      setApagarLocal(null);
      return recarregar();
    },
    onError: (e) => {
      setApagarLocal(null);
      erro(e);
    },
  });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'padrao', rotulo: t('campos.depositoPadrao'), tipo: 'booleano', largura: 'meia', ajuda: t('ajuda.depositoPadrao') },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', largura: 'meia' },
  ];
  const atual = editando && editando !== 'novo' ? editando : null;

  return (
    <>
      {s.pode('estoque.criar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setEditando('novo')}>
            {t('acoes.novoDeposito')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
        {(d) => (
          <div className="grid gap-4 lg:grid-cols-2">
            {d.itens.map((dep) => (
              <Cartao
                key={dep.id}
                titulo={
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate" title={dep.nome}>{dep.nome}</span>
                    {dep.padrao && <Selo cor="acento">{t('status.padrao')}</Selo>}
                    {!dep.ativo && <Selo>{t('status.inativo')}</Selo>}
                  </span>
                }
                acao={
                  <span className="flex items-center gap-1">
                    {s.pode('estoque.editar') && !dep.padrao && dep.ativo && (
                      <Botao variante="fantasma" tamanho="sm" aria-label={t('acoes.definirPadrao')} title={t('acoes.definirPadrao')} icone={<Star className="size-4" />} onClick={() => padrao.mutate(dep.id)} />
                    )}
                    {s.pode('estoque.editar') && <Botao variante="fantasma" tamanho="sm" aria-label={tn('comum.editar')} icone={<Pencil className="size-4" />} onClick={() => setEditando(dep)} />}
                    {s.pode('estoque.excluir') && !dep.padrao && <Botao variante="fantasma" tamanho="sm" aria-label={tn('comum.excluir')} icone={<Trash2 className="size-4" />} onClick={() => setApagar(dep)} />}
                  </span>
                }
              >
                <dl className="mb-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-tinta-3">{t('deposito.produtos')}</dt>
                    <dd className="numero mt-0.5 font-medium">{dep.produtos_em_estoque}</dd>
                  </div>
                  <div>
                    <dt className="text-tinta-3">{t('campos.valorEstoque')}</dt>
                    <dd className="numero mt-0.5 font-medium">{anunciarOcultos(dinheiro(dep.valor_estoque_centavos))}</dd>
                  </div>
                </dl>
                <p className="kicker mb-2">{t('deposito.locais')}</p>
                <div className="flex flex-wrap items-center gap-2">
                  {dep.locais.length === 0 && <span className="text-sm text-tinta-3">{t('deposito.semLocais')}</span>}
                  {dep.locais.map((l) => (
                    <span key={l.id} title={l.descricao ? textoLivre(l.descricao) : undefined} className="numero inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-borda px-2.5 py-0.5 text-xs text-tinta-2">
                      <span className="min-w-0 wrap-anywhere">{l.codigo}</span>
                      {s.pode('estoque.excluir') && (
                        // r8 visual B6: alvo de 24px (era o X de 12px) e exclusão com confirmação.
                        <button type="button" aria-label={`${tn('comum.excluir')} ${l.codigo}`} onClick={() => setApagarLocal(l)} className="-my-1 -mr-1.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full text-tinta-3 hover:bg-sup-3 hover:text-perigo">
                          <X className="size-3.5" aria-hidden />
                        </button>
                      )}
                    </span>
                  ))}
                  {s.pode('estoque.criar') && (
                    <form
                      className="flex items-center gap-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const codigo = (novoLocal[dep.id] ?? '').trim();
                        if (codigo) addLocal.mutate({ deposito: dep.id, codigo });
                      }}
                    >
                      <Entrada aria-label={t('acoes.novoLocal')} placeholder={t('acoes.novoLocal')} value={novoLocal[dep.id] ?? ''} onChange={(e) => setNovoLocal((x) => ({ ...x, [dep.id]: e.target.value }))} className="h-8 w-32" maxLength={40} />
                      <Botao type="submit" tamanho="sm" aria-label={t('acoes.adicionar')} icone={<Plus className="size-4" />} />
                    </form>
                  )}
                </div>
              </Cartao>
            ))}
          </div>
        )}
      </Estado>
      <Modal
        aberto={!!editando}
        aoFechar={() => setEditando(null)}
        titulo={atual ? atual.nome : t('acoes.novoDeposito')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEditando(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-deposito">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-deposito"
          campos={campos}
          inicial={atual ? { ...atual } : { ativo: true, padrao: false }}
          aoEnviar={async (v) => {
            const corpo = { nome: v.nome, padrao: !!v.padrao, ativo: v.ativo !== false };
            if (atual) await patch(`/estoque/depositos/${atual.id}`, corpo);
            else await post('/estoque/depositos', corpo);
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
            setEditando(null);
          }}
        />
      </Modal>
      <Confirmar aberto={!!apagarLocal} aoFechar={() => setApagarLocal(null)} aoConfirmar={() => apagarLocal && rmLocal.mutate(apagarLocal.id)} carregando={rmLocal.isPending} perigoso titulo={t('confirmar.excluirLocal', { codigo: apagarLocal?.codigo ?? '' })} texto={t('confirmar.excluirLocalTexto')} />
      <Confirmar aberto={!!apagar} aoFechar={() => setApagar(null)} aoConfirmar={() => apagar && remover.mutate(apagar.id)} carregando={remover.isPending} perigoso titulo={t('confirmar.excluirDeposito')} texto={t('confirmar.excluirDepositoTexto')} />
    </>
  );
}
